import type { UserWorkEngagementStatus } from "./user-work-engagement.types";

export interface WorkSummary {
  idWork: number;
  title: string;
  cleanUrl: string;
  synopsis: string;
  marketingText: string;
  overviewTitle: string;
  imageUrl: string;
  imageUrlLarge: string | null;
  rating: number;
  engagementStatus?: UserWorkEngagementStatus;
  ratingCount: number;
  reviewCount: number;
  totalEditions: number;
  lowestEditionPrice: number | null;
  collectibleLowPrice: number;
  isVideo: boolean;
  idProductType: number;
}

export interface WorkAuthor {
  idAuthor: number;
  name: string;
  url: string;
}

export interface WorkSeries {
  idSeries: number;
  name: string;
  url: string;
  number: string | null;
  isFollowing: boolean;
}

export interface WorkBreadcrumb {
  name: string;
  url: string;
}

export interface WorkAuthorFeature {
  idAuthor: number;
  name: string;
  url: string;
  description: string;
  isFollowing: boolean;
}

export enum WorkAuthorSeriesWidgetType {
  Series = 1,
  Author = 2,
}

export interface WorkSeriesFeature {
  idSeries: number;
  name: string;
  url: string;
  isFollowing: boolean;
}

export interface WorkAuthorSeriesItem {
  idWork: number;
  title: string;
  authors: string;
  imageUrl: string;
  url: string;
  price: number;
}

export interface WorkAuthorSeriesWidget {
  type: WorkAuthorSeriesWidgetType;
  description: string;
  author: WorkAuthorFeature | null;
  series: WorkSeriesFeature | null;
  items: WorkAuthorSeriesItem[];
}

export interface WorkCondition {
  bindingIdMedia: number;
  idMedia: number;
  idQuality: number;
  name: string;
  idIq: number | null;
  idAmazon: number;
  idWishList?: number;
  isbn: string;
  ean: string;
  upc: string;
  price: number | null;
  listPrice: number | null;
  quantity: number;
  inCartQuantity: number;
  imageUrl: string;
  imageUrlLarge?: string | null;
  imageUrlExtraLarge?: string | null;
  isExLibrary: boolean | null;
  hasNoDustJacket: boolean | null;
  hasNoCd: boolean | null;
  bestAvailableMessage?: string;
  hasShipCountryRestriction: boolean;
  shippingSurcharge: number | null;
  estimatedShippingMessage: string | null;
  loyaltyBonusPoints: number;
  isThriftDeal: boolean;
  onSaleDate: string | null;
  isBackorder: boolean;
  isPreOrder: boolean;
  editionDetails: WorkEditionDetails | null;
  editionDetailItems?: WorkEditionDetailItem[];
  overviewHtml?: string;
}

export interface WorkEditionDetailItem {
  key: string;
  label: string;
  value: string;
}

export interface WorkEditionDetails {
  hasFirstChapter: boolean;
  title: string;
  format: string;
  language: string;
  releaseDate: string | null;
  publisher: string;
  numberOfPages: number;
  weight: number;
  height: number;
  width: number;
  length: number;
  ageRange: string;
  gradeRange: string;
  isLargePrint: boolean;
  rating: string;
  actors: string;
  directors: string;
  artists: string;
  idBrand: number;
  brandName: string;
  runtime: number;
  speed: string;
  numberOfTracks: number;
  numberOfItems: number;
  additionalVideoInfo: string;
}

export interface WorkTag {
  name: string;
  url: string;
  noFollow: boolean;
}

export interface WorkBlogMention {
  idBlogPage: number;
  title: string;
  url: string;
  thumbnailImageUrl: string;
  preview: string;
  authorName: string;
  postedAt: string;
}

export interface WorkCrossProductItem {
  idWork: number;
  title: string;
  blurb: string;
  url: string;
  imageUrl: string;
  minPrice: number;
  maxPrice: number;
  isCollectible: boolean;
}

export interface WorkCrossProductWidget {
  title: string;
  items: WorkCrossProductItem[];
}

export interface WorkProfessionalReview {
  review: string;
  reviewerName: string;
  fromOtherEdition: boolean;
}

export interface WorkFirstChapter {
  firstChapter: string;
  fromOtherEdition: boolean;
}

export interface WorkFormat {
  idMedia: number;
  name: string;
  lowestPrice: number | null;
  highestPrice: number | null;
  listItemCount: number;
  copiesPerSixMonths: number;
  fallbackCondition?: WorkCondition;
  conditions: WorkCondition[];
}

export interface WorkReview {
  idWorkReview: number;
  rating: number;
  customerName: string;
  headline: string;
  body: string;
  postedAt: string;
  verifiedPurchase: boolean;
  likes: number;
  userVoteValue: number;
  isCurrentUser: boolean;
  isVisible: boolean;
}

export interface WorkReviewPage {
  reviews: WorkReview[];
  pageNumber: number;
  hasMore: boolean;
}

export interface WorkPageData {
  work: WorkSummary | null;
  authors: WorkAuthor[];
  series: WorkSeries[];
  breadcrumbs: WorkBreadcrumb[];
  authorFeature: WorkAuthorFeature | null;
  authorSeriesWidget: WorkAuthorSeriesWidget | null;
  formats: WorkFormat[];
  reviews: WorkReview[];
  relatedSubjects: WorkTag[];
  blogMentions: WorkBlogMention[];
  crossProductWidget: WorkCrossProductWidget | null;
  purchasedWorkMessage: WorkPurchasedMessage | null;
  selectedMedia: number;
  selectedQuality: number;
  selectionResolved: boolean | null;
  redirectUrl: string | null;
}

export interface WorkPurchasedMessage {
  datePurchased: string;
  orderNumber: string;
  didWriteReview: boolean;
}

export interface WorkSelectorData {
  formats: WorkFormat[];
  selectedMedia: number;
  selectedQuality: number;
}
